/**
 * OrgLeaderboard — shared leaderboard used by both the Impact page and the
 * Organization page. Renders the same list of top users (rank, username,
 * impact level badge) sourced from `GET /api/leaderboards`, with a
 * Weekly/All-Time toggle and a View More/View Less collapse.
 *
 * The `/leaderboards` endpoint is org-scoped server-side
 * (`{"org_id": current_user.org_id}` in routes/social.py:199), so no extra
 * filtering is needed on the client.
 *
 * Props:
 *   currentUserId  — id of the logged-in user, used to render the "(You)"
 *                    label + row highlight. Optional; omit to render every
 *                    row identically.
 *   initialPeriod  — 'weekly' | 'all-time'. Defaults to 'weekly' to match
 *                    ImpactPages.js's prior behavior.
 *   limit          — max rows to request from the backend. Default 20,
 *                    matching the value ImpactPages was already passing.
 *   initialVisible — how many rows to show before the "View More" collapse
 *                    kicks in. Default 3, matching ImpactPages. Set to
 *                    a larger number (e.g. 5) on pages that want a fuller
 *                    list by default.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from '../../utils/api';
import { Medal, Gem, Trophy, Star, ChevronDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEAL_ACCENT = '#5DBFAD';
const TEAL_LIGHT = '#E8F5F3';

const badgeConfig = {
  Bronze:   { icon: Medal,  color: '#CD7F32', bg: 'rgba(205, 127, 50, 0.12)' },
  Silver:   { icon: Medal,  color: '#A8A8A8', bg: 'rgba(168, 168, 168, 0.12)' },
  Gold:     { icon: Medal,  color: '#D4AF37', bg: 'rgba(212, 175, 55, 0.12)' },
  Platinum: { icon: Gem,    color: '#9D5CFF', bg: 'rgba(157, 92, 255, 0.12)' },
  Hero:     { icon: Trophy, color: '#F5A623', bg: 'rgba(245, 166, 35, 0.12)' },
};

const ImpactBadge = ({ level }) => {
  const cfg = badgeConfig[level] || { icon: Star, color: TEAL_ACCENT, bg: TEAL_LIGHT };
  const Icon = cfg.icon;
  return (
    <div
      style={{
        width: 28, height: 28, borderRadius: 8, background: cfg.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <Icon size={16} color={cfg.color} strokeWidth={1.5} />
    </div>
  );
};

const OrgLeaderboard = ({
  currentUserId = null,
  initialPeriod = 'weekly',
  limit = 20,
  initialVisible = 3,
}) => {
  const [period, setPeriod] = useState(initialPeriod);
  const [rows, setRows] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/leaderboards?period=${period}&limit=${limit}`);
        if (!cancelled) setRows(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to fetch leaderboard:', error);
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [period, limit]);

  const visibleRows = useMemo(
    () => (showAll ? rows : rows.slice(0, initialVisible)),
    [rows, showAll, initialVisible]
  );

  return (
    <div data-testid="org-leaderboard">
      {/* Header: title + Weekly/All-Time toggle */}
      <div
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1F2937', margin: 0 }}>
          Leaderboard
        </h3>
        <div style={{ display: 'flex', background: '#F3F4F6', borderRadius: 8, padding: 2 }}>
          <button
            type="button"
            data-testid="leaderboard-period-weekly"
            onClick={() => setPeriod('weekly')}
            style={{
              padding: '6px 12px', fontSize: 12, fontWeight: 500, borderRadius: 6,
              border: 'none', cursor: 'pointer',
              background: period === 'weekly' ? '#FFFFFF' : 'transparent',
              color: period === 'weekly' ? '#1F2937' : '#6B7280',
              boxShadow: period === 'weekly' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            Weekly
          </button>
          <button
            type="button"
            data-testid="leaderboard-period-all-time"
            onClick={() => setPeriod('all-time')}
            style={{
              padding: '6px 12px', fontSize: 12, fontWeight: 500, borderRadius: 6,
              border: 'none', cursor: 'pointer',
              background: period === 'all-time' ? '#FFFFFF' : 'transparent',
              color: period === 'all-time' ? '#1F2937' : '#6B7280',
              boxShadow: period === 'all-time' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            All-Time
          </button>
        </div>
      </div>

      {/* Rows */}
      <div style={{ background: '#FFFFFF', borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div
            data-testid="leaderboard-loading"
            style={{ padding: '16px', fontSize: 13, color: '#6B7280', textAlign: 'center' }}
          >
            Loading…
          </div>
        ) : rows.length === 0 ? (
          <div
            data-testid="leaderboard-empty"
            style={{ padding: '16px', fontSize: 13, color: '#6B7280', textAlign: 'center' }}
          >
            No leaderboard data yet.
          </div>
        ) : (
          <>
            {visibleRows.map((entry, index) => {
              const isMe = currentUserId && entry.user_id === currentUserId;
              const isLast = index === visibleRows.length - 1;
              return (
                <div
                  key={entry.user_id}
                  data-testid="leaderboard-row"
                  style={{
                    display: 'flex', alignItems: 'center', padding: '12px 16px',
                    borderBottom: !isLast ? '1px solid #F3F4F6' : 'none',
                    background: isMe ? TEAL_LIGHT : 'transparent',
                  }}
                >
                  <div
                    style={{
                      width: 32, height: 32, borderRadius: 8,
                      background:
                        entry.rank === 1 ? '#FEF3C7'
                        : entry.rank === 2 ? '#F3F4F6'
                        : entry.rank === 3 ? '#FED7AA'
                        : '#F9FAFB',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: entry.rank <= 3 ? 16 : 13, fontWeight: 600,
                      color: '#374151', marginRight: 12,
                    }}
                  >
                    {entry.rank === 1 ? '🥇'
                      : entry.rank === 2 ? '🥈'
                      : entry.rank === 3 ? '🥉'
                      : entry.rank}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>
                      {entry.username}
                      {isMe && (
                        <span style={{ marginLeft: 6, fontSize: 11, color: TEAL_ACCENT }}>
                          (You)
                        </span>
                      )}
                    </div>
                  </div>
                  <ImpactBadge level={entry.impact_level} />
                </div>
              );
            })}

            {rows.length > initialVisible && (
              <button
                type="button"
                data-testid="leaderboard-view-more-btn"
                onClick={() => setShowAll(v => !v)}
                style={{
                  width: '100%', padding: '12px', background: '#F9FAFB', border: 'none',
                  borderTop: '1px solid #E5E7EB', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  fontSize: 13, fontWeight: 500, color: TEAL_ACCENT,
                }}
              >
                {showAll
                  ? 'View Less'
                  : `View More (${rows.length - initialVisible} more)`}
                <ChevronDown
                  size={16}
                  style={{
                    transform: showAll ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OrgLeaderboard;
